import { lowerFirst } from 'lodash';

const LOCKED = 423;
const SERVICE_UNAVAILABLE = 503;
const UNPROCESSABLE_CONTENT = 422;

const ORGANIZATION_COUPLING_IN_PROGRESS_ERROR_CODE = 'OrganizationCouplingInProgress';
const ORGANIZATION_SYNC_UNAVAILABLE_TITLE = 'Organization synchronization unavailable';
const ORGANIZATION_SYNC_DOMAIN_RULE_ERROR_CODES: readonly string[] = [
  'OrganizationSyncBulkUnavailable',
  'OrganizationSyncCreationRejected',
  'OrganizationSyncHomeownerAssociationActivityNotInitialized',
  'OrganizationSyncHomeownerAssociationWorkspaceArchived',
  'OrganizationSyncInvalidEmailAddress',
  'OrganizationSyncInvalidIban',
  'OrganizationSyncInvalidPhoneNumber',
  'OrganizationSyncMergeUnavailable',
  'OrganizationSyncNotMapped',
  'OrganizationSyncOperationConflict',
  'OrganizationSyncPartyNotFound',
  'OrganizationSyncTagInUse',
  'OrganizationSyncTagNameTaken',
  'OrganizationSyncTeamNotMapped',
  'OrganizationSyncValidationFailed',
  'OrganizationSyncVersionConflict',
];

type UpstreamError = {
  errorCode: string | null;
  status: number | null;
  title: string | null;
};

const isRecord = (value: unknown): value is { [key: string]: unknown } =>
  !!value && typeof value === 'object';

const getString = (value: unknown) => (typeof value === 'string' ? value : null);

const getNumber = (value: unknown) => (typeof value === 'number' ? value : null);

/**
 * Reads the KeypointConnect API status, error code and ProblemDetails title from an error, whether it is a tRPC
 * client error (formatted by the BFF), an Axios error or an already unwrapped `{ status, data }` error.
 *
 * @param error - Error thrown by a query or mutation.
 * @returns The upstream status, error code and title; each is null when the error does not carry it.
 */
const getUpstreamError = (error: unknown): UpstreamError => {
  if (!isRecord(error)) return { errorCode: null, status: null, title: null };

  const data = isRecord(error.data) ? error.data : null;
  const response = isRecord(error.response) ? error.response : null;
  const responseData = response && isRecord(response.data) ? response.data : null;
  const apiErrorData = data && isRecord(data.apiErrorData) ? data.apiErrorData : null;
  const problemDetails = apiErrorData ?? responseData ?? data;

  return {
    errorCode:
      getString(data?.apiErrorCode) ??
      getString(responseData?.errorCode) ??
      getString(data?.errorCode),
    status: getNumber(data?.httpStatus) ?? getNumber(response?.status) ?? getNumber(error.status),
    title: getString(problemDetails?.title),
  };
};

/**
 * Returns the `errors` translation key for a KeypointConnect API refusal caused by the coupling of the Team to the
 * central relationship management (Organization): a CRM write while the Team is being coupled (423), a change the
 * central relationship management could not confirm (503), or a change, creation or deletion of a Company, Contact or
 * homeowner association that it refused or that a coupled Team cannot do yet, such as a bulk import or a merge (422).
 *
 * @param error - Error thrown by a query or mutation.
 * @returns The translation key, or null when the error is not an Organization coupling or synchronization refusal.
 */
const getOrganizationSyncErrorMessageKey = (error: unknown) => {
  const { errorCode, status, title } = getUpstreamError(error);

  if (status === LOCKED || errorCode === ORGANIZATION_COUPLING_IN_PROGRESS_ERROR_CODE) {
    return lowerFirst(ORGANIZATION_COUPLING_IN_PROGRESS_ERROR_CODE);
  }

  if (status === SERVICE_UNAVAILABLE && title === ORGANIZATION_SYNC_UNAVAILABLE_TITLE) {
    return 'organizationSyncUnavailable';
  }

  if (
    status === UNPROCESSABLE_CONTENT &&
    errorCode &&
    ORGANIZATION_SYNC_DOMAIN_RULE_ERROR_CODES.includes(errorCode)
  ) {
    return lowerFirst(errorCode);
  }

  return null;
};

export default getOrganizationSyncErrorMessageKey;
